import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useOrganization } from "../../../context/OrganizationContext";
import { usePermissions } from "../../../context/PermissionsContext";
import {
  AlertBox,
  PrimaryButton,
  SecondaryButton,
  SkeletonBoard,
} from "../../UI/DesignStandards";
import {
  cardPaymentsStatusLabel,
  formatCardPaymentsHint,
  getCardPaymentsDashboard,
  getCardPaymentsStatus,
  hasStripePublishableKey,
  startCardPaymentsSession,
} from "../../../utility/cardPayments";
import ConnectPanel from "./ConnectPanel";

const CardPayments = () => {
  const { currentOrganization } = useOrganization();
  const { canManageOrganization } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [openingDashboard, setOpeningDashboard] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("not_started");
  const [requirementHints, setRequirementHints] = useState([]);
  const clientConfigured = hasStripePublishableKey();

  const organizationId = currentOrganization?.id;

  const loadStatus = async ({ quiet = false } = {}) => {
    if (!organizationId) return;
    setError("");
    if (!quiet) setLoading(true);
    try {
      const data = await getCardPaymentsStatus(organizationId);
      setStatus(data.status || "not_started");
      setRequirementHints(data.requirementHints || []);
    } catch (loadError) {
      if (loadError.message?.includes("not configured")) {
        setStatus("not_configured");
        setRequirementHints([]);
      } else {
        setError(loadError.message || "Could not load card payments.");
      }
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  useEffect(() => {
    if (!organizationId) return;
    if (!canManageOrganization) {
      setLoading(false);
      return;
    }
    loadStatus();
  }, [organizationId, canManageOrganization]);

  const handleStart = async () => {
    if (!organizationId) return;
    setStarting(true);
    setError("");
    try {
      const data = await startCardPaymentsSession(organizationId);
      setStatus(data.status || "not_started");
    } catch (startError) {
      setError(startError.message || "Could not start card payments.");
    } finally {
      setStarting(false);
    }
  };

  const handleDashboard = async () => {
    if (!organizationId) return;
    setOpeningDashboard(true);
    setError("");
    try {
      const data = await getCardPaymentsDashboard(organizationId);
      const url = data.dashboardUrl || "https://dashboard.stripe.com";
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (dashboardError) {
      setError(dashboardError.message || "Could not open Stripe Dashboard.");
    } finally {
      setOpeningDashboard(false);
    }
  };

  if (loading) {
    return (
      <div
        className="w-full max-w-4xl mx-auto space-y-6"
        role="status"
        aria-label="Loading card payments"
      >
        <SkeletonBoard rows={4} />
      </div>
    );
  }

  if (!canManageOrganization) {
    return (
      <div className="w-full max-w-4xl mx-auto">
        <div className="text-center py-12">
          <h2 className="heading-display text-2xl mb-2">Access denied</h2>
          <p className="text-brand-muted">
            You need permission to manage this organization.
          </p>
        </div>
      </div>
    );
  }

  const configured = status !== "not_configured";
  const started = status === "needs_information" || status === "ready";
  const showOnboarding = status !== "ready";

  return (
    <div className="w-full max-w-4xl mx-auto space-y-6">
      <header className="card-board overflow-hidden">
        <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
          <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
            Card payments
          </h1>
        </div>
      </header>

      {error ? (
        <AlertBox type="error" title="There was a problem" message={error} />
      ) : null}

      <div className="card-board p-6 space-y-4">
        <div>
          <div className="text-sm font-medium text-brand-muted">Status</div>
          <div className="text-lg font-display font-bold text-brand-charcoal">
            {cardPaymentsStatusLabel(status)}
          </div>
        </div>
        {!clientConfigured ? (
          <p className="text-sm text-brand-muted">
            Card payments is not configured on this site.
          </p>
        ) : (
          <>
            <p className="text-sm text-brand-muted">
              {status === "ready" ? (
                <>
                  This organization is the merchant of record. Turning on Card
                  payments means a bowler who pays with Pay now can Cancel
                  until 24 hours before the squad. This app refunds the
                  advertised entry and the 2% platform fee. Card processing is
                  not refunded. You cannot turn that window off. Pay now
                  estimates card processing at Stripe’s typical US rate of
                  2.9% plus 30 cents so you net the advertised entry. If
                  Stripe takes more, you receive less than advertised. If
                  Stripe takes less, you receive more. A Pay now refund still
                  returns the advertised entry and the 2% platform fee. It
                  does not use Stripe’s actual processing. See the{" "}
                  <Link
                    to="/terms-of-service"
                    className="underline text-brand-gold-ink"
                  >
                    Terms of Service
                  </Link>
                  , the{" "}
                  <Link
                    to="/refund-policy"
                    className="underline text-brand-gold-ink"
                  >
                    Refund policy
                  </Link>
                  , and Stripe’s Connected Account Agreement.
                </>
              ) : (
                <>
                  Set up card payments means you agree to the{" "}
                  <Link
                    to="/terms-of-service"
                    className="underline text-brand-gold-ink"
                  >
                    Terms of Service
                  </Link>
                  , the{" "}
                  <Link
                    to="/refund-policy"
                    className="underline text-brand-gold-ink"
                  >
                    Refund policy
                  </Link>
                  , and Stripe’s Connected Account Agreement. The organization
                  is the merchant of record. A bowler who pays with Pay now can
                  Cancel until 24 hours before the squad. This app refunds the
                  advertised entry and the 2% platform fee. Card processing is
                  not refunded. You cannot turn that window off. Pay now
                  estimates card processing at Stripe’s typical US rate of 2.9%
                  plus 30 cents so you net the advertised entry. If Stripe
                  takes more, you receive less than advertised. If Stripe takes
                  less, you receive more. A Pay now refund still returns the
                  advertised entry and the 2% platform fee. It does not use
                  Stripe’s actual processing.
                </>
              )}
            </p>
            {configured && started && showOnboarding ? (
              <p className="text-sm text-brand-muted">
                Click Add information. Stripe may open a new window so you can
                finish setup or sign in to Stripe. If it doesn’t appear, allow
                pop-ups for this site. Tax monitoring and Climate can stay Off.
              </p>
            ) : configured && !started ? (
              <p className="text-sm text-brand-muted">
                Stripe will ask you to add information. A new window will
                open. If it doesn’t appear, allow pop-ups for this site. Sign
                in to Stripe if it asks.
              </p>
            ) : null}
          </>
        )}
        <div className="flex flex-wrap gap-3">
          {configured && clientConfigured && !started ? (
            <PrimaryButton onClick={handleStart} loading={starting}>
              Set up card payments
            </PrimaryButton>
          ) : null}
          {configured && status === "ready" ? (
            <SecondaryButton
              onClick={handleDashboard}
              disabled={openingDashboard}
            >
              {openingDashboard ? "Opening…" : "Open Stripe Dashboard"}
            </SecondaryButton>
          ) : null}
        </div>
      </div>

      {requirementHints.length > 0 && status === "needs_information" ? (
        <AlertBox
          type="warning"
          title="Stripe still needs information"
          message={requirementHints.map(formatCardPaymentsHint).join(" ")}
        />
      ) : null}

      {started && clientConfigured ? (
        <div className="card-board p-6">
          <ConnectPanel
            organizationId={organizationId}
            showOnboarding={showOnboarding}
            onExited={() => loadStatus({ quiet: true })}
          />
        </div>
      ) : null}
    </div>
  );
};

export default CardPayments;
